<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/Overview';
    let nextLink= '/Examples/Building_an_Interpreter/Basic_Evaluation_Functions';
    
    import Note from "$lib/notes/Note.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="building-an-interpreter-1"
  bind:intersecting={visibleById["building-an-interpreter-1"]}
  onIntersectChange={(visible) => setVisible("building-an-interpreter-1", visible)}
>Building an Interpreter</SectionComponent>
<p>This example demonstrates how to <strong>create an interpreter</strong> for your Freon-based language.</p>
<SectionComponent tag="h2" 
  id="the-computer-aided-learning-dsl-2"
  bind:intersecting={visibleById["the-computer-aided-learning-dsl-2"]}
  onIntersectChange={(visible) => setVisible("the-computer-aided-learning-dsl-2", visible)}
>The Computer Aided Learning DSL</SectionComponent>
<p>The language used in this extended example is the same one explained in detail in the <a
  href="/Tutorial/Overview"
>tutorial</a>.<br>
In the DSL’s requirements, our hypothetical client wanted a way to <strong>test the page flow</strong>.<br>
We’ll build an interpreter to do just that.</p>
<p>To follow along, run:</p>
<pre class="language-bash"><code class="language-bash">npm create freon@latest</code></pre>
<p>Then select the language <strong>EducationInterpreter</strong>.<br>
Build the project (<code>npm run build</code>), start the editor, and select the model named <strong>Model4Interpreter</strong> —<br>
it’s essentially the same as the tutorial’s <code>Lesson6</code> model.</p>
<p>Open the model unit <strong>TestB</strong>, then run the interpreter from the <strong>Edit</strong> menu on the entire test<br>
(by selecting the test as the current node).</p>
<Note>{#snippet header()}The selected node is the one that is interpreted{/snippet}
{#snippet content()}
When running the interpreter from the <code>Edit</code> menu, Freon evaluates the <strong>currently selected node</strong>.  
You’ll see different results in the <em>Interpreter</em> tab depending on the selection.  
Sometimes interpretation cannot complete because additional context is needed — it’s up to the interpreter creator to handle such cases.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="values-for-the-literals-3"
  bind:intersecting={visibleById["values-for-the-literals-3"]}
  onIntersectChange={(visible) => setVisible("values-for-the-literals-3", visible)}
>Values for the Literals</SectionComponent>
<p>As explained in <a
  href="/Documentation/Interpreter_Framework"
>Interpreter Framework</a>,<br>
there’s only one file we need to modify:<br>
<code>/src/freon/interpreter/EducationInterpreter.ts</code>.</p>
<p>We’ll build the evaluation <strong>from the bottom up</strong> — starting with the literal expressions:<br>
<code>SimpleNumber</code>, <code>NumberLiteralExpression</code>, and <code>Fraction</code>.</p>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/EducationInterpreter.ts#L216-L228

/////////////////// Literals

override evalSimpleNumber(node: SimpleNumber, ctx: InterpreterContext): RtObject &#123;
    return new RtNumber(node.value)
&#125;

override evalNumberLiteralExpression(node: NumberLiteralExpression, ctx: InterpreterContext): RtObject &#123;
    return new RtNumber(node.value)
&#125;

override evalFraction(node: Fraction, ctx: InterpreterContext): RtObject &#123;
    return new RtFraction(new RtNumber(node.numerator), new RtNumber(node.denominator))
&#125;</code></pre>
<p>The first two functions return an <code>RtNumber</code> runtime object containing the node’s value.<br>
The third returns an <code>RtFraction</code>, defined as:</p>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/runtime/RtFraction.ts

import &#123; RtBoolean, RtNumber, RtObject &#125; from "@freon4dsl/core";

export class RtFraction extends RtObject &#123;
    readonly _type: string = "RtFraction";

    nominator: RtNumber;
    denumerator: RtNumber;

    constructor(num: RtNumber, den: RtNumber) &#123;
        super();
        this.nominator = num;
        this.denumerator = den;
    &#125;

    override equals(other: RtObject): RtBoolean &#123;
        if (isRtFraction(other)) &#123;
            return this.nominator.equals(other.nominator).and(this.denumerator.equals(other.denumerator));
        &#125; else &#123;
            return RtBoolean.FALSE;
        &#125;
    &#125;
    
    override toString(): string &#123;
        return this.nominator.toString() + "/" + this.denumerator.toString()
    &#125;
&#125;

export function isRtFraction(object: any): object is RtFraction &#123;
    const _type = (object as any)?._type;
    return !!_type &amp;&amp; _type === "RtFraction";
&#125;</code></pre>
<p>For our purposes, we don’t need to simplify fractions (e.g., <code>6/3 → 2</code>).<br>
We just need to compare them for equality, which is handled by the <code>equals</code> method.</p>
<SectionComponent tag="h2" 
  id="values-for-binary-expressions-4"
  bind:intersecting={visibleById["values-for-binary-expressions-4"]}
  onIntersectChange={(visible) => setVisible("values-for-binary-expressions-4", visible)}
>Values for Binary Expressions</SectionComponent>
<p>Next, let’s define the evaluation functions for <strong>binary expressions</strong>.<br>
They all follow a similar pattern. Here’s the <code>OrExpression</code>:</p>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/EducationInterpreter.ts#L244-L248

override evalOrExpression(node: OrExpression, ctx: InterpreterContext): RtObject &#123;
    const left = main.evaluate(node.left, ctx) as RtBoolean
    const right = main.evaluate(node.right, ctx) as RtBoolean
    return left.or(right)
&#125;</code></pre>
<p>We first evaluate both sides of the expression.<br>
Note the use of the <strong>main interpreter</strong> from <code>MainEducationInterpreter.ts</code>,<br>
which can evaluate any node type.<br>
We then use the built-in <code>or</code> function of the <code>RtBoolean</code> class, defined as:</p>
<pre class="language-ts"><code class="language-ts">export class RtBoolean extends RtObject &#123;
	static readonly TRUE = new RtBoolean(true);
	static readonly FALSE = new RtBoolean(false);

	static of(bool: boolean): RtBoolean &#123;
		return bool ? RtBoolean.TRUE : RtBoolean.FALSE;
	&#125;

	or(other: RtBoolean): RtBoolean &#123;
		return RtBoolean.of(this._value || other.asBoolean());
	&#125;
    ...  
&#125;		</code></pre>
<p>Other expressions, like <code>AndExpression</code> and <code>EqualsExpression</code>, are implemented in much the same way.<br>
For example, here’s the <code>GreaterOrEqualsExpression</code>:</p>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/EducationInterpreter.ts#L256-L260

override evalGreaterOrEqualsExpression(node: GreaterOrEqualsExpression, ctx: InterpreterContext): RtObject &#123;
    const left = main.evaluate(node.left, ctx) as RtNumber
    const right = main.evaluate(node.right, ctx) as RtNumber
    return RtBoolean.of(left.value &gt;= right.value)
&#125;</code></pre>
<p>This pattern makes your interpreter modular, easy to extend, and consistent across expression types.</p>


<PrevNextSection {prevLink} {nextLink} />